import { type ReactNode, useState } from 'react';

import { MatterStatsPopover } from './matter-stats-popover';
import { UserStatsPopover } from './user-stats-popover';
import { UsageActivityBreakdownResponseDto } from '../../../../../backend/src/usage/dto/usage-activity-breakdown.response.dto';
import { useDownloadMatterStats } from '../../../api/mutations/useDownloadMatterStats';
import { useDownloadUserStats } from '../../../api/mutations/useDownloadUserStats';
import formatDate from '../../../helpers/formatDate';
import { Icon } from '../../components/icon';
import { Button } from '../../components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/shadcn/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '../../components/shadcn/empty';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/shadcn/popover';
import { Skeleton } from '../../components/skeleton';
import { cn } from '../../lib/utils';

type Breakdown = UsageActivityBreakdownResponseDto;

const numberFormatter = new Intl.NumberFormat();

const MAX_ROWS = 8;

function LeaderboardRow({
  rank,
  label,
  kimThreads,
  fraction,
  popoverContent,
}: {
  rank: number;
  label: string;
  kimThreads: number;
  fraction: number;
  popoverContent?: ReactNode;
}) {
  const content = (
    <>
      {/* Proportional activity bar */}
      <span
        className="absolute inset-y-1 left-0 rounded-md bg-gray-200"
        style={{ width: `${Math.max(fraction * 100, 2)}%` }}
        aria-hidden
      />
      <span className="relative w-4 shrink-0 text-xs font-semibold text-gray-400 tabular-nums">{rank}</span>
      <span className="relative flex-1 truncate text-sm font-medium text-gray-700 group-hover:text-gray-900">
        {label}
      </span>
      <span className="relative shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
        {numberFormatter.format(kimThreads)}
      </span>
      {popoverContent && (
        <Icon className="lucide--chevron-right relative h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
      )}
    </>
  );

  const baseClass = 'group relative flex items-center gap-3 overflow-hidden rounded-md px-3 py-2';

  if (popoverContent) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={cn(baseClass, 'w-full cursor-pointer text-left')}>
            {content}
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-72">
          {popoverContent}
        </PopoverContent>
      </Popover>
    );
  }
  return <div className={baseClass}>{content}</div>;
}

function LeaderboardCard<T>({
  title,
  icon,
  rows,
  isLoading,
  getKey,
  getLabel,
  getKimThreads,
  renderPopover,
  modalDescription,
  modalAction,
}: {
  title: string;
  icon: string;
  rows: T[];
  isLoading: boolean;
  getKey: (row: T) => string;
  getLabel: (row: T) => string;
  getKimThreads: (row: T) => number;
  renderPopover?: (row: T) => ReactNode;
  // Shown under the title inside the "view all" modal (e.g. the time period).
  modalDescription?: ReactNode;
  // Shown next to the title inside the "view all" modal (e.g. a download button).
  modalAction?: ReactNode;
}) {
  const [showAll, setShowAll] = useState(false);
  const maxValue = Math.max(1, ...rows.map(getKimThreads));
  const visibleRows = rows.slice(0, MAX_ROWS);

  const renderRow = (row: T, i: number) => (
    <LeaderboardRow
      key={getKey(row)}
      rank={i + 1}
      label={getLabel(row)}
      kimThreads={getKimThreads(row)}
      fraction={getKimThreads(row) / maxValue}
      popoverContent={renderPopover ? renderPopover(row) : undefined}
    />
  );

  return (
    <div className="flex flex-col rounded-lg border bg-gray-50 p-5">
      <div className="mb-4 flex items-center justify-between gap-2.5">
        <div>
          <div className="flex items-center gap-2">
            <Icon className={cn(icon, 'size-3')} />
            <p className="text-sm">{title}</p>
          </div>
          <p className="text-xs text-gray-500">Ranked by Kim threads</p>
        </div>
        {!isLoading && rows.length > 0 && (
          <Button variant="ghost" size="sm" className="text-gray-600" onClick={() => setShowAll(true)}>
            View all
            <Icon className="lucide--chevron-right" />
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full bg-gray-50" />
          ))}
        </div>
      ) : visibleRows.length === 0 ? (
        <Empty className="gap-3 py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Icon className={cn(icon, 'size-5')} />
            </EmptyMedia>
            <EmptyTitle className="text-sm font-medium">No Kim threads in this period</EmptyTitle>
            <EmptyDescription className="text-xs">
              Activity will appear here once users start chatting with Kim.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="-mx-2 flex flex-col gap-0.5">{visibleRows.map(renderRow)}</div>
      )}

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className={cn(icon, 'size-4')} />
              {title}
            </DialogTitle>
            {modalDescription && <DialogDescription>{modalDescription}</DialogDescription>}
          </DialogHeader>
          <div className="-mx-2 max-h-[60vh] overflow-y-auto">
            <div className="flex flex-col gap-0.5">{rows.map(renderRow)}</div>
          </div>
          {modalAction && <div className="mt-4 flex justify-end border-t border-gray-100 pt-4">{modalAction}</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ActivityBreakdown({
  breakdown,
  isLoading,
  organisationId,
  startDate,
  endDate,
  isViewingOtherOrg = false,
}: {
  breakdown: Breakdown | undefined;
  isLoading: boolean;
  organisationId: string;
  startDate?: Date;
  endDate?: Date;
  // When viewing another org (global admin), matter names are anonymised to their IDs.
  isViewingOtherOrg?: boolean;
}) {
  const { fetchDownload: fetchUserDownload, isLoading: isDownloading } = useDownloadUserStats();
  const { fetchDownload: fetchMatterDownload, isLoading: isMatterDownloading } = useDownloadMatterStats();

  const periodLabel =
    startDate && endDate
      ? `Showing activity from ${formatDate(startDate)} to ${formatDate(endDate)}.`
      : 'Showing all-time activity.';

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <LeaderboardCard
        title="Most Active Users"
        icon="lucide--users"
        rows={breakdown?.users ?? []}
        isLoading={isLoading}
        getKey={(u) => u.userId}
        getLabel={(u) => u.email}
        getKimThreads={(u) => u.kimThreads}
        renderPopover={(u) => (
          <UserStatsPopover
            userId={u.userId}
            email={u.email}
            organisationId={organisationId}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        modalDescription={periodLabel}
        modalAction={
          <Button
            variant="default"
            size="sm"
            disabled={isDownloading}
            onClick={() => fetchUserDownload({ organisationId, startDate, endDate })}
          >
            <Icon className={isDownloading ? 'lucide--loader-circle animate-spin' : 'lucide--download'} />
            Download
          </Button>
        }
      />
      <LeaderboardCard
        title="Most Active Matters"
        icon="lucide--folder-open"
        rows={breakdown?.matters ?? []}
        isLoading={isLoading}
        getKey={(m) => m.matterId}
        getLabel={(m) => (isViewingOtherOrg ? m.matterId : m.matterName)}
        getKimThreads={(m) => m.kimThreads}
        renderPopover={(m) => (
          <MatterStatsPopover
            matterId={m.matterId}
            matterName={isViewingOtherOrg ? m.matterId : m.matterName}
            organisationId={organisationId}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        modalDescription={periodLabel}
        modalAction={
          <Button
            variant="default"
            size="sm"
            disabled={isMatterDownloading}
            onClick={() => fetchMatterDownload({ organisationId, startDate, endDate })}
          >
            <Icon className={isMatterDownloading ? 'lucide--loader-circle animate-spin' : 'lucide--download'} />
            Download
          </Button>
        }
      />
    </div>
  );
}
